import {
  MESSAGE_PURPOSE__SIGNUP,
  MESSAGE_PURPOSE__RESET_PASSWORD,
  MESSAGE_PURPOSE__PEP_SIGNUP,
  MESSAGE_PURPOSE__PEP_LOGIN,
  MESSAGE_PURPOSE__PEP_RESET_PASSWORD,
  MESSAGE_PURPOSE__PEP_USERNAME,
  MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE,
  MESSAGE_PURPOSE__PEP_USERNAME_CODE,
  MESSAGE_PURPOSE__PEP_REGISTRATION_CODE,
} from './TextMessageTemplates';

/**
 * TECH-2730. The variables each system message actually supplies at send time.
 *
 * SINGLE SOURCE OF TRUTH for both the save-time validator and the editor, so
 * the two cannot drift.
 *
 * WHY THIS EXISTS. `@zedoc/text` substitutes the literal `[unknown]` for any
 * variable it is not given (`packages/text/src/text.js:121`), which is correct
 * and deliberate behaviour. But nothing told an author which variables exist,
 * so a typo produced a patient-facing email with a dead link and NO warning -
 * a broken account-recovery path discovered only by the patient.
 *
 * The sets DIFFER BY PURPOSE, and that is the trap: the staff reset-password
 * message supplies `action_url`, while the PEP reset-password message - same
 * name, different surface - supplies `linkToShorten`. An author who reasonably
 * carries one over to the other gets a dead link.
 *
 * Keep each list in step with the code that builds the variables:
 *   staff  -> buildAccountMessageVars, accounts/server/accountEmailTemplates.js
 *   PEP    -> the `variables` objects in patient-app-graphql patient.resolver.ts
 */

// accounts/server/index.js buildAccountMessageVars()
const STAFF_ACCOUNT_VARIABLES = [
  'name',
  'email',
  'product_name',
  'product_owner_name',
  'action_url',
  'site_url',
  'logo_url',
];

export const MESSAGE_TEMPLATE_VARIABLES = {
  [MESSAGE_PURPOSE__SIGNUP]: STAFF_ACCOUNT_VARIABLES,
  [MESSAGE_PURPOSE__RESET_PASSWORD]: STAFF_ACCOUNT_VARIABLES,
  // patient.resolver.ts sendSignUpRequest
  [MESSAGE_PURPOSE__PEP_SIGNUP]: ['linkToShorten'],
  [MESSAGE_PURPOSE__PEP_LOGIN]: ['linkToShorten'],
  // patient.resolver.ts resetPEPPasswordRequest
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD]: ['linkToShorten'],
  // patient.resolver.ts userListPEPRequest - userName is exposed as a variable
  // because an override cannot interpolate it the way the built-in copy does.
  [MESSAGE_PURPOSE__PEP_USERNAME]: ['linkToShorten', 'userName'],
  // TECH-2730 Phase 2. The OTP messages carry NO link - they carry a code the
  // patient types back into a page they already have open. Declaring
  // `linkToShorten` here would be the carry-over mistake this file warns
  // about: it would save, and render as "[unknown]" in a patient's inbox.
  //   password-reset.service.ts   sendPasswordResetEmail
  //   user-management.service.ts  sendUsernameRecoveryEmail
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE]: ['otp'],
  [MESSAGE_PURPOSE__PEP_USERNAME_CODE]: ['otp'],
  //   auth.service.ts  sendRegistrationEmail
  [MESSAGE_PURPOSE__PEP_REGISTRATION_CODE]: ['otp'],
};

/**
 * TECH-2730. Variables an override MUST contain, by purpose.
 *
 * This deliberately does NOT overturn the 2026-09-19 decision that message
 * content is the administrator's responsibility. It narrows it to the three
 * cases where omitting the variable does not produce worse copy — it produces
 * a message that cannot do its job at all.
 *
 * The OTP messages carry a six-digit code and nothing else of use. Phase 2
 * moved that code out of the source literal (`${otp}`) and into a template
 * variable (`{{otp}}`), because an organisation's override cannot run
 * JavaScript. So an override that omits `{{otp}}` sends a verification email
 * WITH NO CODE IN IT, and registration, password reset and username recovery
 * all dead-end for that tenant — with the editor reporting a clean save.
 *
 * A missing `{{linkToShorten}}` is still only a dead link on three of the
 * four link messages, and is still the administrator's business there.
 *
 * THE FOURTH IS NOT. TECH-2730 F8 (2026-09-28) made the staff-sent
 * reset-password link carry a real, single-use token: the link IS the reset.
 * Before F8 it pointed at the self-service start page, so an override that
 * dropped it merely sent the patient somewhere they could type a username;
 * after F8 an override without `{{linkToShorten}}` sends a message that
 * cannot reset anything, the same class as an OTP message without its code.
 * So `pep_reset_password` joins the required list. Jubayer's decision,
 * 2026-09-30, narrowing the 2026-09-19 rule by exactly one purpose.
 */
export const REQUIRED_TEMPLATE_VARIABLES = {
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD_CODE]: ['otp'],
  [MESSAGE_PURPOSE__PEP_USERNAME_CODE]: ['otp'],
  [MESSAGE_PURPOSE__PEP_REGISTRATION_CODE]: ['otp'],
  [MESSAGE_PURPOSE__PEP_RESET_PASSWORD]: ['linkToShorten'],
};

// Mirrors the substitution pattern in packages/text/src/text.js so the
// validator sees exactly what the renderer will: an identifier, an optional
// trailing "!", and an optional ":modifier".
const TOKEN_PATTERN = /{{\s*(\w+)(!)?(:\w+)?\s*}}/g;

/**
 * Every distinct variable name referenced by a template string.
 * @param {string} source
 * @returns {string[]}
 */
export const extractTemplateVariables = (source) => {
  if (typeof source !== 'string' || !source) {
    return [];
  }
  const found = [];
  let match = TOKEN_PATTERN.exec(source);
  while (match) {
    if (!found.includes(match[1])) {
      found.push(match[1]);
    }
    match = TOKEN_PATTERN.exec(source);
  }
  TOKEN_PATTERN.lastIndex = 0;
  return found;
};

/**
 * Variables referenced by a template that the send path will NOT supply, and
 * which would therefore render as the literal `[unknown]`.
 *
 * A purpose with no declared variables is not validated - returning nothing is
 * deliberate, so an unrecognised purpose cannot block a save.
 *
 * @param {string} purpose
 * @param {string[]} sources template strings (subject, content, htmlContent, ...)
 * @returns {string[]} unknown variable names, in first-seen order
 */
export const findUnknownTemplateVariables = (purpose, sources) => {
  const allowed = MESSAGE_TEMPLATE_VARIABLES[purpose];
  if (!allowed) {
    return [];
  }
  const unknown = [];
  (sources || []).forEach((source) => {
    extractTemplateVariables(source).forEach((name) => {
      if (!allowed.includes(name) && !unknown.includes(name)) {
        unknown.push(name);
      }
    });
  });
  return unknown;
};

export default MESSAGE_TEMPLATE_VARIABLES;

/**
 * Required variables absent from every source. Absent from ALL of them, not
 * from each: a translation legitimately carries the code in only one field.
 *
 * @param {string} purpose
 * @param {string[]} sources
 * @returns {string[]}
 */
export const findMissingRequiredVariables = (purpose, sources) => {
  const required = REQUIRED_TEMPLATE_VARIABLES[purpose];
  if (!required || required.length === 0) {
    return [];
  }
  const present = new Set();
  (sources || []).forEach((source) => {
    extractTemplateVariables(source).forEach((name) => present.add(name));
  });
  return required.filter((name) => !present.has(name));
};
